import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { cardHeight, screenDevices, subTabs, tabs } from 'src/enums';
import { RootState } from 'src/redux/rootReducer';
import { useLazyGetUsersClientQuery } from 'src/redux/services/clients';
import { TUserWithRole } from 'src/redux/services/users';
import { IQueryPropsWithClientId } from 'src/redux/types';
import { ConfirmationModal, IConfirmationModalProps } from '../modal/ConfirmationModal';
import { ListItems } from '../shared/listElements';
import { IUserCardProps, UserCard } from './UserCard';
import { TAppSlice } from 'src/redux/appSlice';

const mapStateToProps = (state: RootState) => ({
  screenSize: state.app.screenSize,
  startRoutePath: state.app.startRoutePath,
});

interface IUsersListProps {
  screenSize: TAppSlice['screenSize'];
  startRoutePath: TAppSlice['startRoutePath'];
}

const UsersListComponent: FC<IUsersListProps> = ({ screenSize, startRoutePath }) => {
  const { appId = '', clientId = '' } = useParams<{ appId: string; clientId: string }>();
  const navigate = useNavigate();
  const [height, setHeight] = useState(0);
  const [modalProps, setModalProps] = useState<IConfirmationModalProps>({
    isOpen: false,
    onAction: () => {},
    onClose: () => {},
    title: '',
    actionButtonText: '',
    mainMessage: [],
  });
  const [getUsers] = useLazyGetUsersClientQuery();
  const query = (offset: number, search?: string): IQueryPropsWithClientId => {
    return {
      query: {
        limit: '10',
        offset,
        search: search || '',
      },
      clientId: appId,
    };
  };

  useEffect(() => {
    setHeight(getCardHeight(screenSize));
  }, [screenSize]);

  const getCardHeight = useCallback(
    (screenSize: number) => {
      switch (screenSize) {
        case screenDevices.Desktop:
          return cardHeight.Desktop;
        case screenDevices.Tablet:
          return 160;
        case screenDevices.MiniTablet:
          return 190;
        case screenDevices.Mobile:
          return 210;
        default:
          return 170;
      }
    },
    [screenDevices, cardHeight],
  );

  const createUserButton = useMemo(
    () => (
      <Tooltip title="Создать пользователя">
        <Button
          data-id="create-user-button"
          className="text-15-medium"
          variant="custom"
          component="label"
          color="secondary"
          onClick={() => navigate(`/${startRoutePath}/${appId}/${tabs.users}/${subTabs.create}`)}
          startIcon={<PersonAddAltOutlinedIcon />}
        />
      </Tooltip>
    ),
    [navigate],
  );

  const handleEventClick = (userId?: number) => {
    navigate(`/${startRoutePath}/${appId}/${tabs.users}/${userId}`);
  };

  return (
    <Box data-id="users" className="wrapper">
      <Box className="content">
        <ListItems<TUserWithRole, IQueryPropsWithClientId, IUserCardProps>
          heightListItem={height}
          padding={cardHeight.Indent}
          query={query}
          getItems={getUsers}
          RowElement={UserCard}
          searchFormChildren={createUserButton}
          rowElementProps={{
            height: height,
            padding: cardHeight.Indent,
            onClick: handleEventClick,
            setConfirmModalProps: setModalProps,
          }}
        />
        <ConfirmationModal
          isOpen={modalProps.isOpen}
          onAction={modalProps.onAction}
          onClose={modalProps.onClose}
          title={modalProps.title}
          actionButtonText={modalProps.actionButtonText}
          mainMessage={modalProps.mainMessage}
        />
      </Box>
    </Box>
  );
};

export const UsersList = connect(mapStateToProps)(UsersListComponent);
